/**
 * A country guess from the visitor's IP (PLAN.md §11).
 *
 * Country of residence is the first real question the funnel asks, it decides
 * the phone prefix under it, and for the great majority of people the answer is
 * the country they are sitting in. Filling it in is two fewer interactions on
 * the step with the worst drop-off.
 *
 * It is a *hint and nothing more*. The field stays fully editable, the guess is
 * dropped the moment the user touches it, and eligibility is still decided by
 * `POST /account` server-side. An IP is a poor witness — VPNs, roaming, mobile
 * carriers routing through another country — so nothing may depend on it being
 * right, and nothing here is ever sent to the server as fact.
 *
 * ## Why Cloudflare's trace, and why not our own host
 *
 * There is no IP-to-country database on the funnel's side to ask: the API runs
 * on plain EC2, so no `CF-IPCountry` header reaches it, and shipping MaxMind or
 * DB-IP into the image means a licence, a build-time download and tens of
 * megabytes for one optional prefill.
 *
 * Cloudflare already does the lookup for anything it fronts, and exposes it at
 * `/cdn-cgi/trace` with `access-control-allow-origin: *`. The funnel's own
 * domains are not proxied — `my.kcmtra.de/cdn-cgi/trace` is a 404 and the
 * staging host answers it with the SPA's own index.html — so this asks the
 * marketing site, which is. It is a public page of ours, the request carries no
 * credentials and no identifier, and the response is two letters.
 */

/**
 * Cloudflare-fronted and ours. Public marketing, so naming it in the bundle
 * discloses nothing; the funnel and CRM hostnames stay out of the client.
 */
const TRACE_URL = 'https://www.kcmtrade.com/cdn-cgi/trace'

/**
 * Short on purpose. This races the user: a guess that lands after they have
 * already picked a country is worthless, and one that lands after they have
 * started typing would be an interruption. Better to give up than to be late.
 */
const TIMEOUT_MS = 1500

/** Per tab, so a resumed step or a StrictMode remount does not ask again. */
const CACHE_KEY = 'kcm.geo.country'

/**
 * Answers Cloudflare will give for an address it cannot place: `XX` for unknown
 * and `T1` for Tor. Both are real two-letter codes in the response and neither
 * is a country anyone lives in.
 */
const NOT_A_COUNTRY = new Set(['XX', 'T1'])

/**
 * Pulls `loc` out of the `key=value` lines Cloudflare returns.
 *
 * Strict about the shape rather than trusting the body: a host that is not
 * behind Cloudflare still answers `/cdn-cgi/trace`, usually with an SPA's
 * index.html and a 200. Requiring a two-letter `loc=` line means that case
 * yields nothing instead of nonsense.
 */
export function parseTraceCountry(body: string): string | undefined {
  for (const line of body.split('\n')) {
    if (!line.startsWith('loc=')) continue
    const code = line.slice(4).trim().toUpperCase()
    if (!/^[A-Z]{2}$/.test(code) || NOT_A_COUNTRY.has(code)) return undefined
    return code
  }
  return undefined
}

/**
 * The visitor's ISO 3166-1 alpha-2, or `undefined` when we cannot tell.
 *
 * Never throws and never rejects. Every failure — offline, blocked by an ad
 * blocker, timed out, refused — is the same outcome as not knowing, which the
 * form already handles: it shows an empty picker, exactly as it did before.
 */
export async function detectCountry(storage: Storage = sessionStorage): Promise<string | undefined> {
  const cached = readCache(storage)
  // `''` is a cached miss — asked once, got nothing. Worth remembering, or a
  // user with a blocker pays the timeout on every mount.
  if (cached !== undefined) return cached || undefined

  let country: string | undefined
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
  try {
    const res = await fetch(TRACE_URL, {
      // Nothing of ours should ride along to another origin, and the answer is
      // per-IP rather than per-user so there is nothing to cache either.
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      signal: controller.signal,
    })
    if (res.ok) country = parseTraceCountry(await res.text())
  } catch {
    /* an unknown country, by another name */
  } finally {
    clearTimeout(timer)
  }

  try {
    storage.setItem(CACHE_KEY, country ?? '')
  } catch {
    /* private browsing; the lookup still worked, it just runs again */
  }
  return country
}

/** `undefined` when never asked, `''` when asked and unanswered. */
function readCache(storage: Storage): string | undefined {
  try {
    return storage.getItem(CACHE_KEY) ?? undefined
  } catch {
    return undefined
  }
}
